import { useEffect } from "react";
import { SITE_URL } from "../data/site";

/**
 * Lightweight SEO helper. Sets <title>, meta description, canonical, robots,
 * Open Graph tags and JSON-LD blocks per route. No external dependency.
 */
function setMeta(attr, key, content) {
  let m = document.querySelector(`meta[${attr}="${key}"]`);
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute(attr, key);
    document.head.appendChild(m);
  }
  m.setAttribute("content", content);
}

export default function Seo({ title, description, canonical, jsonLd, noindex = false,
  image, type = "website", publishedTime, modifiedTime }) {
  useEffect(() => {
    if (title) {
      document.title = title;
      setMeta("property", "og:title", title);
      setMeta("name", "twitter:title", title);
    }
    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }
    // Schema-only instances (e.g. FAQ) must not overwrite page metadata.
    if (title || canonical || description) {
      const imageUrl = image ? new URL(image, SITE_URL).href : `${SITE_URL}/tsb-logo.jpg`;
      setMeta("property", "og:type", type);
      setMeta("property", "og:image", imageUrl);
      setMeta("property", "og:image:alt", title || "Texan Strong Builds");
      setMeta("name", "twitter:image", imageUrl);
      setMeta("name", "twitter:image:alt", title || "Texan Strong Builds");
      for (const [key, value] of [["article:published_time", publishedTime], ["article:modified_time", modifiedTime]]) {
        if (type === "article" && value) setMeta("property", key, value);
        else document.querySelectorAll(`meta[property="${key}"]`).forEach((meta) => meta.remove());
      }
    }
    if (canonical) {
      // Canonical URLs always use the configured public domain, never preview,
      // tracking parameters, fragments, or duplicate trailing-slash variants.
      const path = new URL(canonical, SITE_URL).pathname.replace(/\/+$/, "") || "/";
      const canonicalUrl = `${SITE_URL}${path}`;
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonicalUrl);
      document.querySelectorAll('link[rel="canonical"]').forEach((other) => {
        if (other !== link) other.remove();
      });
      setMeta("property", "og:url", canonicalUrl);
    } else if (noindex) {
      document.querySelectorAll('link[rel="canonical"]').forEach((link) => link.remove());
      document.querySelector('meta[property="og:url"]')?.remove();
    }
    if (noindex) {
      setMeta("name", "robots", "noindex, follow");
    } else if (title || canonical || description) {
      setMeta("name", "robots", "index, follow, max-snippet:-1, max-image-preview:large");
    }
    const scripts = [];
    const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    for (const block of blocks) {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.text = JSON.stringify(block);
      el.setAttribute("data-tsb-jsonld", "1");
      document.head.appendChild(el);
      scripts.push(el);
    }
    return () => {
      scripts.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, [title, description, canonical, noindex, image, type, publishedTime, modifiedTime, JSON.stringify(jsonLd)]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}
